import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { SpecialPriceFormData } from "../../types/specialPrice";

export default function SpecialPriceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<SpecialPriceFormData>({
    fromDate: "",
    toDate: "",
    description: "",
    priceAdjustPers: 0,
    status: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEditMode) {
      fetchSpecialPriceData();
    }
  }, [id]);

  const fetchSpecialPriceData = async () => {
    try {
      const response = await axiosInstance.get(`/special-prices/${id}`);
      setFormData(response.data);
    } catch (error) {
      setError("Failed to fetch special price data");
      console.error("Error fetching special price:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (isEditMode) {
        await axiosInstance.put(`/special-prices/${id}`, formData);
      } else {
        await axiosInstance.post("/special-prices", formData);
      }
      navigate("/special-prices");
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to save special price");
      console.error("Error saving special price:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  return (
    <div className="w-full space-y-6">
      <PageBreadcrumb pageTitle={isEditMode ? "Edit Special Price" : "Add Special Price"} />

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        {error && (
          <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <InputWithLabel
              label="From Date"
              name="fromDate"
              type="date"
              value={formData.fromDate}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="To Date"
              name="toDate"
              type="date"
              value={formData.toDate}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="Price Adjustment (%)"
              name="priceAdjustPers"
              type="number"
              value={formData.priceAdjustPers.toString()}
              onChange={handleInputChange}
              required
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                name="status"
                value={formData.status.toString()}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/special-prices")}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 